import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import type { EmailSettingsResponse, SmtpSettingsInput } from '../../../shared/api.js';
import { api } from '../lib/api';

const QUERY_KEY = ['email-settings'];

type Draft = {
  host: string;
  port: string;
  secure: boolean;
  user: string;
  pass: string;
  from: string;
  send: boolean;
};

function draftOf(data: EmailSettingsResponse): Draft {
  const s = data.settings;
  return {
    host: s?.host ?? '',
    port: String(s?.port ?? 587),
    secure: s?.secure ?? false,
    user: s?.user ?? '',
    pass: '',
    from: s?.from ?? '',
    send: s?.send ?? true,
  };
}

function Form({ data }: { data: EmailSettingsResponse }) {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });
  // The form is remounted by its key when the saved settings change (see EmailSection).
  const [draft, setDraft] = useState<Draft>(() => draftOf(data));
  const patch = (p: Partial<Draft>) => setDraft({ ...draft, ...p });

  const save = useMutation({
    mutationFn: (body: SmtpSettingsInput) => api.emailSettingsSave(body),
    onSuccess: refresh,
  });
  const reset = useMutation({ mutationFn: api.emailSettingsReset, onSuccess: refresh });
  const check = useMutation({ mutationFn: api.emailCheck });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate({
      host: draft.host.trim(),
      port: Number(draft.port) || 587,
      secure: draft.secure,
      user: draft.user.trim(),
      ...(draft.pass ? { pass: draft.pass } : {}),
      from: draft.from.trim(),
      send: draft.send,
    });
  };

  const pill = !data.configured
    ? { cls: 'pill--muted', text: 'не настроено' }
    : data.sendEnabled
      ? { cls: 'pill--ok', text: 'отправка включена' }
      : { cls: 'pill--warn', text: 'отправка выключена' };
  const source = {
    dashboard: 'настройки с этой страницы',
    env: 'настройки из .env сервера',
    none: 'нет настроек',
  }[data.source];

  return (
    <form className="card form" onSubmit={submit}>
      <div className="card__header">
        <h3>SMTP-сервер</h3>
        <span className={`pill ${pill.cls}`}>{pill.text}</span>
      </div>
      <p className="muted small">Действуют: {source}.</p>
      <label>
        Сервер
        <input
          value={draft.host}
          onChange={(e) => patch({ host: e.target.value })}
          placeholder="smtp.example.com"
          required
        />
      </label>
      <label>
        Порт
        <input
          inputMode="numeric"
          value={draft.port}
          onChange={(e) => patch({ port: e.target.value })}
          required
        />
      </label>
      <label className="calendar-list__toggle">
        <input
          type="checkbox"
          checked={draft.secure}
          onChange={(e) =>
            patch({ secure: e.target.checked, port: e.target.checked ? '465' : '587' })
          }
        />
        TLS сразу (порт 465); без галочки — STARTTLS (587)
      </label>
      <label>
        Логин <span className="muted small">(пусто — без входа)</span>
        <input
          value={draft.user}
          onChange={(e) => patch({ user: e.target.value })}
          autoComplete="off"
        />
      </label>
      <label>
        Пароль{' '}
        <span className="muted small">
          {data.settings?.hasPassword ? '(сохранён; пусто — оставить прежний)' : ''}
        </span>
        <input
          type="password"
          value={draft.pass}
          onChange={(e) => patch({ pass: e.target.value })}
          autoComplete="new-password"
        />
      </label>
      <label>
        Отправитель <span className="muted small">(«CyberNika &lt;agent@example.com&gt;»)</span>
        <input
          value={draft.from}
          onChange={(e) => patch({ from: e.target.value })}
          placeholder="CyberNika <agent@example.com>"
          required
        />
      </label>
      <label className="calendar-list__toggle">
        <input
          type="checkbox"
          checked={draft.send}
          onChange={(e) => patch({ send: e.target.checked })}
        />
        Разрешить агенту отправлять письма контактам
      </label>
      {!data.canStoreSecrets && (
        <p className="notice notice--error">
          На сервере не задан <code>SECRETS_KEY</code>: пароль сохранить нельзя.
        </p>
      )}
      {save.isError && <p className="text-error small">{save.error.message}</p>}
      {reset.isError && <p className="text-error small">{reset.error.message}</p>}
      {check.isError && <p className="text-error small">{check.error.message}</p>}
      {check.isSuccess && (
        <p className="small">Сервер отвечает, вход выполнен; отправитель {check.data.from}.</p>
      )}
      <div className="actions-row">
        <button type="submit" className="btn btn--primary" disabled={save.isPending}>
          {save.isPending ? 'Сохраняю…' : 'Сохранить'}
        </button>
        <button
          type="button"
          className="btn"
          disabled={!data.configured || check.isPending}
          onClick={() => check.mutate()}
        >
          {check.isPending ? 'Проверяю…' : 'Проверить связь'}
        </button>
        {data.source === 'dashboard' && (
          <button
            type="button"
            className="btn btn--ghost"
            disabled={reset.isPending}
            onClick={() => {
              if (
                window.confirm(
                  'Удалить сохранённые настройки? Останутся те, что в .env, если есть.',
                )
              )
                reset.mutate();
            }}
          >
            Сбросить
          </button>
        )}
      </div>
    </form>
  );
}

/** Settings → Почта (owner only): the SMTP server the agent sends letters from. */
export function EmailSection() {
  const query = useQuery({ queryKey: QUERY_KEY, queryFn: api.emailSettings });
  return (
    <section>
      <h2>Почта</h2>
      <p className="muted">
        Агент отправляет письма с этого сервера контактам, которых добавил владелец (
        <code>pnpm contacts add … --provider email</code>). Перед каждым письмом агент показывает
        адресата, тему и текст.
      </p>
      {query.isPending && <p className="muted">Загрузка…</p>}
      {query.isError && <p className="text-error">{query.error.message}</p>}
      {query.data && (
        <Form
          key={`${query.data.source}:${JSON.stringify(query.data.settings)}`}
          data={query.data}
        />
      )}
    </section>
  );
}
